import React, { useMemo, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import UserFormFields from './UserFormFields';
import UserPickerList from './UserPickerList';
import { setUserActive, updateUser } from './api';
import {
  AdminUser,
  formFromUser,
  UserFormValue,
  UsersMeta,
} from './types';
import { dangerBtnClass, primaryBtnClass, secondaryBtnClass } from '../styles';

interface IndividualEditUserProps {
  meta: UsersMeta;
}

const IndividualEditUser: React.FC<IndividualEditUserProps> = ({ meta }) => {
  const [selected, setSelected] = useState<AdminUser | null>(null);
  const [value, setValue] = useState<UserFormValue | null>(null);
  const [listKey, setListKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const onPick = (ids: number[], users: AdminUser[]) => {
    const user = users[0] || null;
    setSelected(user);
    setValue(user ? formFromUser(user) : null);
    setError(null);
    setSuccess(null);
  };

  const dirty = useMemo(() => {
    if (!selected || !value) return false;
    const original = formFromUser(selected);
    return JSON.stringify(original) !== JSON.stringify(value);
  }, [selected, value]);

  const save = async () => {
    if (!selected || !value) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateUser(selected.id, value);
      setSelected(updated);
      setValue(formFromUser(updated));
      setSuccess(`Saved ${updated.email}.`);
      setListKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const backToList = () => {
    if (dirty && !window.confirm('You have unsaved changes. Go back to the list anyway?')) {
      return;
    }
    setSelected(null);
    setValue(null);
    setError(null);
    setSuccess(null);
    setListKey((n) => n + 1);
  };

  const toggleActive = async () => {
    if (!selected) return;
    const next = !selected.is_active;
    const verb = next ? 'reactivate' : 'deactivate';
    if (!window.confirm(`${verb[0].toUpperCase()}${verb.slice(1)} ${selected.email}?`)) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await setUserActive(selected.id, next);
      setSelected(updated);
      setValue(formFromUser(updated));
      setSuccess(next ? `Reactivated ${updated.email}.` : `Deactivated ${updated.email}.`);
      setListKey((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${verb} user`);
    } finally {
      setSaving(false);
    }
  };

  if (selected && value) {
    return (
      <div className="max-w-3xl">
        <button
          type="button"
          onClick={backToList}
          disabled={saving}
          className="inline-flex items-center gap-1 text-sm text-[#3da3d4] hover:text-[#2d8ab8] mb-4 disabled:opacity-50"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to list
        </button>
        <h3 className="text-base font-semibold text-[#0C0B0B] mb-1">Edit {selected.email}</h3>
        <p className="text-sm text-[#626262] mb-4">
          Changing Organization also updates the user&apos;s current organization.
        </p>
        {error && (
          <div className="mb-4 rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 rounded-md bg-emerald-50 text-emerald-800 px-3 py-2 text-sm">
            {success}
          </div>
        )}
        <UserFormFields
          meta={meta}
          value={value}
          onChange={setValue}
          mode="edit"
          disabled={saving}
        />
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving || !dirty}
            onClick={() => void save()}
            className={primaryBtnClass}
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
            Save changes
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void toggleActive()}
            className={selected.is_active ? dangerBtnClass : secondaryBtnClass}
          >
            {selected.is_active ? 'Deactivate account' : 'Reactivate account'}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={backToList}
            className={secondaryBtnClass}
          >
            Back to list
          </button>
        </div>
      </div>
    );
  }

  return (
    <UserPickerList
      meta={meta}
      mode="single"
      selectedIds={[]}
      onChange={onPick}
      refreshToken={listKey}
    />
  );
};

export default IndividualEditUser;
